import { signal } from '@preact/signals';
import { db, type OutboxOp } from '@/lib/db';
import { supabase } from '@/lib/supabase';

/** Offline-first write queue. Every mutation is applied to Dexie immediately
 *  and enqueued here; ops replay in order whenever we're online. Client-side
 *  UUIDs make inserts idempotent (PK conflict on replay == already applied). */

export const pendingWrites = signal(0);

let replaying = false;

export async function enqueue(op: Omit<OutboxOp, 'seq' | 'attempts' | 'created_at'>) {
  await db.outbox.add({ ...op, created_at: new Date().toISOString(), attempts: 0 });
  pendingWrites.value = await db.outbox.count();
  void replay();
}

export async function replay() {
  if (replaying || !navigator.onLine) return;
  replaying = true;
  try {
    for (;;) {
      const op = await db.outbox.orderBy('seq').first();
      if (!op) break;
      const ok = await execute(op);
      if (ok === 'done' || ok === 'drop') {
        await db.outbox.delete(op.seq!);
        if (ok === 'drop') console.warn('outbox: dropped op after permanent error', op);
      } else {
        await db.outbox.update(op.seq!, { attempts: op.attempts + 1 });
        break; // transient failure — retry later, keep order
      }
    }
  } finally {
    replaying = false;
    pendingWrites.value = await db.outbox.count();
  }
}

async function execute(op: OutboxOp): Promise<'done' | 'drop' | 'retry'> {
  try {
    let error: { code?: string; message: string } | null = null;
    if (op.op === 'insert') {
      ({ error } = await supabase.from(op.table).insert(op.values!));
      if (error?.code === '23505') return 'done'; // already applied on a previous attempt
    } else if (op.op === 'upsert') {
      ({ error } = await supabase.from(op.table).upsert(op.values!));
    } else if (op.op === 'update') {
      ({ error } = await supabase.from(op.table).update(op.values!).match(op.match!));
    } else {
      ({ error } = await supabase.from(op.table).delete().match(op.match!));
    }
    if (!error) return 'done';
    // RLS/constraint/shape errors won't fix themselves — drop instead of wedging the queue.
    const transient = /fetch|network|timeout|503|502|429/i.test(error.message);
    console.warn('outbox error', op.table, op.op, error);
    return transient ? 'retry' : 'drop';
  } catch (e) {
    console.warn('outbox transport error', e);
    return 'retry'; // network-level failure
  }
}

export function initOutbox() {
  void db.outbox.count().then((n) => (pendingWrites.value = n));
  window.addEventListener('online', () => void replay());
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void replay();
  });
  setInterval(() => void replay(), 45_000);
}
